import { api } from "../utils/api";
import isClassDiaryDemo from "../demo/isClassDiaryDemo";
import {
  labConfirmSpeakerVoice,
  labExcludeVoiceSample,
  labPurgeMeetingVoice,
  labRegisterVoiceSamples,
  labSuggestSpeakersByVoice
} from "../views/SmartMinutes/voiceApiLab";

/**
 * Enfileira processamento de áudio da Ata Inteligente (job na waybee-api).
 * Em demo, o adapter simula pending → success na fila Jobs/myQueue.
 */
const processSmartMinutesJob = async data => {
  const res = await api.post("ClassDiaryV1/smart-minutes/job", data);
  return res.data;
};

/**
 * Reidentifica falantes na transcrição já processada (sem reenviar áudio).
 */
const attributeSmartMinutesSpeakers = async ({ meetingId, draftId }) => {
  const res = await api.post("ClassDiaryV1/smart-minutes/attribute-speakers", {
    meetingId,
    draftId
  });
  return res.data;
};

/**
 * Fallback de voz em localStorage só no demo offline.
 * Em LB auth a falha da API propaga (sem lab silencioso).
 */
async function withVoiceFallback(apiCall, labCall) {
  try {
    const res = await apiCall();
    return res && res.data !== undefined ? res.data : res;
  } catch (e) {
    if (isClassDiaryDemo()) {
      return labCall();
    }
    throw e;
  }
}

const registerVoiceSamples = async ({ meetingId, clusters }) =>
  withVoiceFallback(
    () =>
      api.post("ClassDiaryV1/smart-minutes/voice/register-samples", {
        meetingId,
        clusters
      }),
    () => labRegisterVoiceSamples({ meetingId, clusters })
  );

const confirmSpeakerVoice = async ({
  meetingId,
  speakerClusterId,
  personKey,
  displayName,
  voiceKey = null,
  consent = true
}) =>
  withVoiceFallback(
    () =>
      api.post("ClassDiaryV1/smart-minutes/voice/confirm-speaker", {
        meetingId,
        speakerClusterId,
        personKey,
        displayName,
        voiceKey,
        consent
      }),
    () =>
      labConfirmSpeakerVoice({
        meetingId,
        speakerClusterId,
        personKey,
        displayName,
        voiceKey
      })
  );

const suggestSpeakersByVoice = async ({ meetingId, clusters, limit }) =>
  withVoiceFallback(
    () =>
      api.post("ClassDiaryV1/smart-minutes/voice/suggest", {
        meetingId,
        clusters,
        limit
      }),
    () => labSuggestSpeakersByVoice({ meetingId, clusters })
  );

const excludeVoiceSample = async ({
  meetingId,
  sampleId,
  speakerClusterId,
  turnId
}) =>
  withVoiceFallback(
    () =>
      api.post("ClassDiaryV1/smart-minutes/voice/exclude-sample", {
        meetingId,
        sampleId,
        speakerClusterId,
        turnId
      }),
    () =>
      labExcludeVoiceSample({
        meetingId,
        sampleId,
        speakerClusterId,
        turnId
      })
  );

const purgeMeetingVoice = async ({ meetingId, reason = "meeting_deleted" }) =>
  withVoiceFallback(
    () =>
      api.post("ClassDiaryV1/smart-minutes/voice/purge-meeting", {
        meetingId,
        reason
      }),
    () => labPurgeMeetingVoice({ meetingId, reason })
  );

const listDrafts = async ({ classId, studentRa } = {}) => {
  const res = await api.get("ClassDiaryV1/smart-minutes", {
    params: {
      classId: classId || undefined,
      studentRa: studentRa || undefined
    }
  });
  return res.data;
};

const getDraft = async id => {
  const res = await api.get(
    `ClassDiaryV1/smart-minutes/${encodeURIComponent(id)}`
  );
  return res.data;
};

const upsertDraft = async draft => {
  const res = await api.put("ClassDiaryV1/smart-minutes", draft);
  return res.data;
};

const deleteDraft = async id => {
  const res = await api.delete(
    `ClassDiaryV1/smart-minutes/${encodeURIComponent(id)}`
  );
  return res.data;
};

const getAudioPlayback = async id => {
  const res = await api.get(
    `ClassDiaryV1/smart-minutes/${encodeURIComponent(id)}/audio`
  );
  return res.data;
};

/** Blob do stream autenticado (lab local / proxy). */
const fetchAudioBlob = async id => {
  const res = await api.get(
    `ClassDiaryV1/smart-minutes/${encodeURIComponent(id)}/audio/stream`,
    { responseType: "blob", timeout: 180000 }
  );
  const mime =
    (res.headers && res.headers["content-type"]) ||
    (res.data && res.data.type) ||
    "audio/mpeg";
  if (res.data instanceof Blob) {
    if (res.data.type) return res.data;
    return new Blob([res.data], { type: mime });
  }
  return new Blob([res.data], { type: mime });
};

export default {
  processSmartMinutesJob,
  attributeSmartMinutesSpeakers,
  registerVoiceSamples,
  confirmSpeakerVoice,
  suggestSpeakersByVoice,
  excludeVoiceSample,
  purgeMeetingVoice,
  listDrafts,
  getDraft,
  upsertDraft,
  deleteDraft,
  getAudioPlayback,
  fetchAudioBlob
};
